import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSEO, getBaseUrl } from '../../utils/seo';

const ServicesPage = () => {
  // SEO設定
  useSEO({
    title: '佐世保市の就労継続支援B型事業所おにぎり｜利用者募集中｜弁当製造で社会復帰支援',
    description: '佐世保市・佐々町の就労継続支援B型事業所おにぎりでは利用者を募集中。弁当製造を通じて職業訓練と社会復帰支援。障害者手帳をお持ちの方、まずはお気軽に見学にお越しください。個人のペースで始められる安心の環境。',
    keywords: '佐世保市,就労継続支援B型,利用者募集,おにぎり,弁当製造,障害者就労支援,佐々町,職業訓練,社会復帰支援,見学受付',
    canonical: `${getBaseUrl()}/services`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LocalBusiness",
          "name": "就労継続支援B型事業所おにぎり",
          "image": "https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/6dabeec2e9be537c19e7241578d8cc6c.jpeg",
          "description": "佐世保市・佐々町の就労継続支援B型事業所おにぎり。弁当製造業務による職業訓練と社会復帰支援。利用者募集中。自家栽培野菜を使用した健康的な弁当を製造し、地域配達サービスを提供。",
          "url": `${getBaseUrl()}/services#onigiri`,
          "telephone": "+81-956-59-4059",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "本田原免226-9",
            "addressLocality": "北松浦郡佐々町",
            "addressRegion": "長崎県",
            "postalCode": "857-0311",
            "addressCountry": "JP"
          },
          "areaServed": [
            {
              "@type": "City",
              "name": "佐世保市"
            },
            {
              "@type": "City",
              "name": "佐々町"
            }
          ],
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "09:00",
            "closes": "17:00"
          }
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "ホーム",
              "item": `${getBaseUrl()}/`
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "事業所案内",
              "item": `${getBaseUrl()}/services`
            }
          ]
        },
        {
          "@type": "WebPage",
          "name": "佐世保市の就労継続支援B型事業所おにぎり｜利用者募集中",
          "description": "佐世保市・佐々町の就労継続支援B型事業所おにぎりでは利用者を募集中。弁当製造による職業訓練と社会復帰支援。",
          "url": `${getBaseUrl()}/services`
        }
      ]
    }
  });

  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <a href="/" className="text-2xl font-bold text-teal-600 cursor-pointer">
                グループホームここな
              </a>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="/#home" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                ホーム
              </a>
              <a href="/#about" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                施設について
              </a>
              <a href="/#features" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                特徴
              </a>
              <a href="/#services" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                サービス
              </a>
              <a href="/services" className="text-teal-600 font-semibold transition-colors cursor-pointer">
                事業所案内
              </a>
            </nav>
            {/* デスクトップ: 電話番号表示のみ */}
            <div className="hidden md:flex items-center bg-teal-600 text-white px-6 py-2 rounded-full">
              <i className="ri-phone-line mr-2"></i>
              <span className="whitespace-nowrap">0956-59-4059</span>
            </div>
            {/* モバイル: 電話リンク */}
            <a
              href="tel:0956-59-4059"
              className="md:hidden bg-teal-600 text-white px-6 py-2 rounded-full hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center"
            >
              <i className="ri-phone-line mr-2"></i>
              お問い合わせ
            </a>
          </div>
        </div>
      </header>

      {/* Page Title */}
      <section className="bg-gradient-to-r from-green-500 to-green-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            佐世保市の就労継続支援B型事業所おにぎり｜利用者募集中｜弁当製造で社会復帰支援
          </h1>
          <p className="text-xl text-green-100">
            佐世保市・佐々町で弁当製造による職業訓練と自立支援｜見学随時受付中
          </p>
        </div>
      </section>

      {/* パンくずリスト */}
      <nav className="bg-gray-100 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ol className="flex items-center space-x-2 text-sm text-gray-600">
            <li>
              <a href="/" className="hover:text-teal-600 cursor-pointer">ホーム</a>
            </li>
            <li>
              <i className="ri-arrow-right-s-line"></i>
            </li>
            <li className="text-teal-600 font-medium">事業所案内</li>
          </ol>
        </div>
      </nav>

      {/* 利用者募集中バナー */}
      <section className="py-8 bg-gradient-to-r from-green-50 to-green-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 border-4 border-green-500">
            <div className="text-center">
              <div className="inline-block bg-red-500 text-white px-6 py-2 rounded-full text-lg font-bold mb-4 animate-pulse">
                利用者募集中！
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                佐世保市・佐々町で就労継続支援B型をお探しの方へ
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                弁当製造を通じて、あなたのペースで働く喜びを見つけませんか？<br />
                料理経験がなくても大丈夫。まずはお気軽に見学にお越しください。
              </p>
              <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
                <a
                  href="tel:0956-59-4059"
                  className="bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center"
                >
                  <i className="ri-phone-line mr-2 text-xl"></i>
                  0956-59-4059
                </a>
                <a
                  href="/#contact"
                  className="bg-white text-green-600 border-2 border-green-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-green-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  お問い合わせフォーム
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 就労継続支援 B型事業所おにぎり */}
      <section id="onigiri" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <i className="ri-restaurant-line text-3xl text-green-600"></i>
              </div>
              <h2 className="text-4xl font-bold text-gray-900">佐世保市・佐々町の就労継続支援B型事業所おにぎり｜弁当製造で自立支援</h2>
            </div>
            <div className="bg-green-50 p-6 rounded-lg mb-8">
              <p className="text-lg text-gray-700 mb-2">
                <i className="ri-map-pin-line text-green-600 mr-2"></i>
                <strong>所在地:</strong>〒857-0311 長崎県北松浦郡佐々町本田原免226-9
              </p>
              <p className="text-lg text-gray-700">
                <i className="ri-phone-line text-green-600 mr-2"></i>
                <strong>TEL:</strong>0956-59-4059
              </p>
            </div>
          </div>

          {/* メイン作業写真 - 大きく目立つように */}
          <div className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <img
                src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/6dabeec2e9be537c19e7241578d8cc6c.jpeg"
                alt="佐々町就労継続支援B型事業所おにぎり弁当製造作業風景 - 利用者が盛り付けや調理補助を行う様子、料理経験がない方でも個人のペースで始められる職業訓練"
                className="rounded-lg shadow-lg w-full h-64 object-cover object-top"
              />
              <img
                src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/aae74b4aa2e439a4d4eed37aa15bff79.jpeg"
                alt="就労継続支援B型事業所おにぎり弁当製造 - 自家栽培野菜を使用した健康的な弁当づくり、社会復帰に向けたスキル習得を支援"
                className="rounded-lg shadow-lg w-full h-64 object-cover object-top"
              />
              <img
                src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/f484fe15dff9222b53b8ab000f1293c8.jpeg"
                alt="就労継続支援B型事業所おにぎり完成弁当 - 自家栽培野菜を使用した健康的な弁当、地域配達サービスとイベント対応で地域社会に貢献"
                className="rounded-lg shadow-lg w-full h-64 object-cover object-top"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">佐世保市・佐々町で弁当製造による職業訓練と社会復帰支援</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                <strong>就労継続支援B型</strong>事業所おにぎりでは、弁当製造業務を行っています。料理の経験がない方でも、その人のできる範囲に応じて盛り付けなどの簡単な作業から始められます。自家栽培の新鮮な野菜を使用した健康的な弁当を製造し、地域の皆様にお届けしています。
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-check-double-line text-green-600 text-xl mt-1 mr-3"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">弁当製造業務</h4>
                    <p className="text-gray-700 text-sm">料理経験がなくても、盛り付けなど個人のペースに合わせた簡単な作業から始められます</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-double-line text-green-600 text-xl mt-1 mr-3"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">自家栽培野菜使用</h4>
                    <p className="text-gray-700 text-sm">新鮮な自家栽培野菜を使用した健康的な弁当製造</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-double-line text-green-600 text-xl mt-1 mr-3"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">地域配達サービス</h4>
                    <p className="text-gray-700 text-sm">地域の皆様への弁当配達サービス</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-double-line text-green-600 text-xl mt-1 mr-3"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">地域貢献</h4>
                    <p className="text-gray-700 text-sm">地域イベントへの弁当配達など社会貢献活動</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              {/* 建物外観写真 */}
              <img
                src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/1aad940ce9cd98299968e2b27caaf1d5.jpeg"
                alt="長崎県北松浦郡佐々町本田原免の就労継続支援B型事業所おにぎり建物外観 - 弁当製造による職業訓練と社会復帰支援を行う障害者就労支援施設"
                className="rounded-lg shadow-lg w-full h-80 object-cover object-top"
              />
            </div>
          </div>

          {/* 利用対象者セクション */}
          <div className="bg-green-50 p-8 rounded-lg mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-user-heart-line text-green-600 mr-3"></i>
              こんな方が利用できます
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg">
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-2xl mr-3 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">障害者手帳をお持ちの方</h4>
                    <p className="text-gray-700 text-sm">身体障害者手帳、療育手帳、精神障害者保健福祉手帳をお持ちの方</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg">
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-2xl mr-3 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">佐世保市・佐々町周辺にお住まいの方</h4>
                    <p className="text-gray-700 text-sm">通所可能な範囲にお住まいの方を募集しています</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg">
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-2xl mr-3 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">働く意欲のある方</h4>
                    <p className="text-gray-700 text-sm">料理経験がなくても大丈夫。働きたいという気持ちがあれば歓迎します</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg">
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-2xl mr-3 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">自分のペースで働きたい方</h4>
                    <p className="text-gray-700 text-sm">無理のない範囲で、できることから始められます</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 利用の流れセクション */}
          <div className="bg-white p-8 rounded-lg border-2 border-green-200 mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center flex items-center justify-center">
              <i className="ri-route-line text-green-600 mr-3"></i>
              利用開始までの流れ
            </h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">1</span>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">お問い合わせ</h4>
                <p className="text-gray-700 text-sm">お電話またはお問い合わせフォームからご連絡ください</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">2</span>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">見学</h4>
                <p className="text-gray-700 text-sm">事業所の雰囲気や作業内容を実際にご覧いただけます</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">3</span>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">体験利用</h4>
                <p className="text-gray-700 text-sm">実際に作業を体験していただき、ご自身に合うか確認できます</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">4</span>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">利用開始</h4>
                <p className="text-gray-700 text-sm">契約手続き後、正式に利用開始となります</p>
              </div>
            </div>
          </div>

          {/* 基本情報セクション */}
          <div className="bg-green-50 p-8 rounded-lg mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-information-line text-green-600 mr-3"></i>
              基本情報
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-time-line text-green-600 mr-2"></i>
                  活動時間
                </h4>
                <p className="text-gray-700">月曜日〜金曜日 9:00〜17:00</p>
                <p className="text-gray-600 text-sm mt-2">※土日祝日はお休みです</p>
              </div>
              <div className="bg-white p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-money-dollar-circle-line text-green-600 mr-2"></i>
                  工賃について
                </h4>
                <p className="text-gray-700">作業内容や時間に応じて工賃をお支払いします</p>
                <p className="text-gray-600 text-sm mt-2">※詳細はお問い合わせください</p>
              </div>
              <div className="bg-white p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-restaurant-2-line text-green-600 mr-2"></i>
                  作業内容
                </h4>
                <p className="text-gray-700">弁当の盛り付け、調理補助、配達準備など</p>
                <p className="text-gray-600 text-sm mt-2">※個人のペースに合わせて作業を選べます</p>
              </div>
              <div className="bg-white p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-car-line text-green-600 mr-2"></i>
                  送迎について
                </h4>
                <p className="text-gray-700">送迎サービスについてはご相談ください</p>
                <p className="text-gray-600 text-sm mt-2">※対応可能な範囲がございます</p>
              </div>
            </div>
          </div>

          {/* 活動の魅力セクション */}
          <div className="bg-white p-8 rounded-lg border-2 border-green-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-heart-line text-green-600 mr-3"></i>
              佐世保市・佐々町で誰でも始められる作業環境｜個人のペースで自立支援
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              料理の経験がない方でも安心してご参加いただけます。一人ひとりのできる範囲に応じて、盛り付けや簡単な調理補助など、無理のない作業から始めていただけます。地域の運動会などのイベントに弁当配達を行い、<strong>地域社会との繋がり</strong>を大切にした社会貢献活動も実施しています。
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-green-50 p-6 rounded-lg text-center">
                <i className="ri-hand-heart-line text-4xl text-green-600 mb-3"></i>
                <h4 className="font-semibold text-gray-900 mb-2">個人のペースに合わせた作業</h4>
                <p className="text-gray-700 text-sm">無理なく、できることから始められます</p>
              </div>
              <div className="bg-green-50 p-6 rounded-lg text-center">
                <i className="ri-truck-line text-4xl text-green-600 mb-3"></i>
                <h4 className="font-semibold text-gray-900 mb-2">地域配達</h4>
                <p className="text-gray-700 text-sm">地域の皆様に弁当をお届けします</p>
              </div>
              <div className="bg-green-50 p-6 rounded-lg text-center">
                <i className="ri-community-line text-4xl text-green-600 mb-3"></i>
                <h4 className="font-semibold text-gray-900 mb-2">イベント対応</h4>
                <p className="text-gray-700 text-sm">地域イベントで社会貢献活動</p>
              </div>
            </div>
          </div>

          {/* 他の事業所へのリンク */}
          <div className="mt-8 text-center">
            <a
              href="/services#vegetable"
              className="inline-flex items-center text-green-600 hover:text-green-700 font-medium cursor-pointer"
            >
              <span>野菜栽培B型支援も見る</span>
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </div>
      </section>

      {/* 就労継続支援 野菜栽培B型支援 */}
      <section id="vegetable" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                <i className="ri-plant-line text-3xl text-orange-600"></i>
              </div>
              <h2 className="text-4xl font-bold text-gray-900">佐世保市の就労継続支援 野菜栽培B型支援｜農業で自立支援</h2>
            </div>
            <div className="bg-orange-50 p-6 rounded-lg mb-8">
              <p className="text-lg text-gray-700">
                <i className="ri-map-pin-line text-orange-600 mr-2"></i>
                <strong>活動場所：</strong>農家から借用した畑での野菜栽培
              </p>
            </div>
          </div>

          {/* メイン作業写真 - 高校生との協働作業を大きく目立つように */}
          <div className="mb-12">
            <img
              src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/119871acdb33bd361665bfec879be398.jpeg"
              alt="就労継続支援野菜栽培B型支援と地元高校生の協働作業 - 高校生と利用者が一緒に野菜栽培を行う世代間交流、農業を通じた職業訓練と社会復帰支援、障害者支援と教育の両面で地域社会に貢献"
              className="rounded-lg shadow-lg w-full h-96 object-cover object-top"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">佐世保市で野菜栽培による職業訓練と社会復帰支援</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                農家から借用した畑で<strong>野菜栽培</strong>を行っています。農作業を通じて自然と体力がつき、健康的な身体づくりができます。また、地元高校生との協働により、世代を超えた貴重な交流機会となっています。
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-check-double-line text-orange-600 text-xl mt-1 mr-3"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">体力向上</h4>
                    <p className="text-gray-700 text-sm">農作業を通じて自然と体力がつき、健康的な身体づくりができます</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-double-line text-orange-600 text-xl mt-1 mr-3"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">高校生との交流</h4>
                    <p className="text-gray-700 text-sm">地元高校生との協働により、世代を超えた貴重な交流機会を提供</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-double-line text-orange-600 text-xl mt-1 mr-3"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">無人販売所</h4>
                    <p className="text-gray-700 text-sm">地域への新鮮野菜の提供</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-double-line text-orange-600 text-xl mt-1 mr-3"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">循環型事業</h4>
                    <p className="text-gray-700 text-sm">弁当製造への食材供給で循環型事業を実現</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://static.readdy.ai/image/508267c310485ed668a070497aa72a0d/285c3b9c3d4458edbedab27553624d94.jpeg"
                alt="就労継続支援野菜栽培B型支援作業風景 - 農家借用畑での野菜栽培作業、利用者が農作業に取り組む様子、体力向上と職業訓練を通じた社会復帰支援、無人販売所で地域に新鮮野菜を提供"
                className="rounded-lg shadow-lg w-full h-80 object-cover object-top"
              />
            </div>
          </div>

          <div className="bg-orange-50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-heart-line text-orange-600 mr-3"></i>
              佐世保市で体力向上と世代間交流｜農業を通じた自立支援
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              月に数回、地元高校生が授業の一環として<strong>野菜栽培</strong>を手伝い、障害者支援と教育の両面で<strong>地域社会に貢献</strong>しています。農作業を通じて自然と体力がつき、高校生との交流により社会性も育まれます。栽培した野菜は無人販売所で地域の皆様に提供するとともに、弁当製造の食材としても活用し、循環型の事業を展開しています。
            </p>
            <div className="grid md:grid-cols-3 gap-6 mt-6">
              <div className="bg-white p-4 rounded-lg text-center">
                <i className="ri-heart-pulse-line text-3xl text-orange-600 mb-2"></i>
                <p className="font-semibold text-gray-900">体力向上・健康づくり</p>
              </div>
              <div className="bg-white p-4 rounded-lg text-center">
                <i className="ri-team-line text-3xl text-orange-600 mb-2"></i>
                <p className="font-semibold text-gray-900">高校生との交流</p>
              </div>
              <div className="bg-white p-4 rounded-lg text-center">
                <i className="ri-store-2-line text-3xl text-orange-600 mb-2"></i>
                <p className="font-semibold text-gray-900">無人販売所</p>
              </div>
            </div>
          </div>

          {/* 他の事業所へのリンク */}
          <div className="mt-8 text-center">
            <a
              href="/services#onigiri"
              className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium cursor-pointer"
            >
              <span>就労継続支援B型事業所おにぎりも見る</span>
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </div>
      </section>

      {/* グループホームへのリンク */}
      <section className="py-12 bg-teal-50">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">佐世保市グループホームここなの居室設備｜プライバシー重視の完全個室</h3>
          <p className="text-gray-700 mb-6">
            約10畳の完全個室とユニットバス完備で、<strong>プライバシーを重視</strong>した生活環境を提供しています。
          </p>
          <a
            href="/#features"
            className="inline-block bg-teal-600 text-white px-8 py-3 rounded-full hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            居室設備の詳細を見る
          </a>
        </div>
      </section>

      {/* おにぎり専用CTAセクション */}
      <section className="py-20 bg-gradient-to-r from-green-500 to-green-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="inline-block bg-white text-green-600 px-6 py-2 rounded-full text-lg font-bold mb-6">
            利用者募集中！
          </div>
          <h2 className="text-4xl font-bold text-white mb-6">まずはお気軽に見学にお越しください</h2>
          <p className="text-xl text-green-100 mb-8 leading-relaxed">
            佐世保市・佐々町で就労継続支援B型をお探しの方、<br />
            弁当製造を通じて働く喜びを見つけませんか？<br />
            料理経験がなくても大丈夫です。
          </p>
          <div className="flex flex-col md:flex-row gap-4 justify-center">
            {/* デスクトップ: 電話番号表示のみ */}
            <div className="hidden md:inline-flex bg-white text-green-600 px-8 py-4 rounded-full text-lg font-semibold items-center justify-center">
              <i className="ri-phone-line mr-2 text-xl"></i>
              0956-59-4059
            </div>
            {/* モバイル: 電話リンク */}
            <a
              href="tel:0956-59-4059"
              className="md:hidden bg-white text-green-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
            >
              <i className="ri-phone-line mr-2 text-xl"></i>
              0956-59-4059
            </a>
            <a
              href="/#contact"
              className="bg-green-700 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-green-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              お問い合わせフォーム
            </a>
          </div>
          <p className="text-green-100 mt-6">
            <i className="ri-time-line mr-2"></i>
            受付時間：月〜金 9:00〜17:00
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h4 className="font-bold mb-4">グループホームここな</h4>
              <div className="text-gray-300">
                <p>〒858-0926</p>
                <p>長崎県佐世保市大潟町725-87</p>
                <p>TEL: 0956-59-4059</p>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4">関連事業所</h4>
              <div className="text-gray-300">
                <a
                  href="/services#onigiri"
                  className="block font-semibold mb-2 hover:text-white transition-colors cursor-pointer"
                >
                  就労継続支援B型事業所おにぎり
                </a>
                <p>〒857-0311</p>
                <p>長崎県北松浦郡佐々町本田原免226-9</p>
                <a
                  href="/services#vegetable"
                  className="block font-semibold mt-4 hover:text-white transition-colors cursor-pointer"
                >
                  就労継続支援 野菜栽培B型支援
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4">運営会社</h4>
              <div className="text-gray-300">
                <p className="font-semibold">株式会社ブレインズ</p>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-700 text-center text-gray-400">
            <p>&copy; 2024 グループホームここな. All rights reserved.</p>
            <a
              href="https://readdy.ai/?origin=logo"
              className="text-gray-400 hover:text-white transition-colors"
              rel="nofollow"
            >
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServicesPage;
